import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import {
  Activity,
  AlertTriangle,
  CreditCard,
  Database,
  DollarSign,
  FolderOpen,
  Users,
} from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import { useNavigate } from "react-router";
import PageHeader from "../../../common/components/PageHeader";
import { toast } from "../../../common/components/Toast";
import { SPACING } from "../../../common/constants/spacing";
import { useAdminDashboardStats, useSubscriptionStats } from "../../api/admin";
import { StatCard } from "../../shared/components/StatCard";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

function Dashboard() {
  const { t } = useTranslation("admin_panel");
  const { stats, error } = useAdminDashboardStats();
  const { subscriptionStats, error: subError } = useSubscriptionStats();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      toast.error("Failed to load dashboard statistics");
      console.error("Error fetching dashboard stats:", error);
    }
    if (subError) {
      console.error("Error fetching subscription stats:", subError);
    }
  }, [error, subError]);

  // Prepare chart data for users growth
  const chartData = {
    labels: stats?.users_growth_data?.map((item) => item.month_name) || [],
    datasets: [
      {
        label: "New Users",
        data: stats?.users_growth_data?.map((item) => item.count) || [],
        borderColor: "#1976d2",
        backgroundColor: "#1976d2aa",
        tension: 0.1,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "User Growth Over Time",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <>
      <PageHeader
        title={t("dashboard.title")}
        description={t("dashboard.description")}
      />

      {/* Main Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("dashboard.totalUsers")}
            value={stats?.total_users}
            icon={Users}
            color="#1976d2"
            trend={stats?.recent_activity?.new_users_last_30_days}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("dashboard.totalProjects")}
            value={stats?.total_projects}
            icon={FolderOpen}
            color="#ed6c02"
            trend={stats?.recent_activity?.new_projects_last_30_days}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("dashboard.totalSources")}
            value={stats?.total_sources}
            icon={Database}
            color="#2e7d32"
            trend={stats?.recent_activity?.new_sources_last_30_days}
          />
        </Grid>
      </Grid>

      {/* Subscription Stats */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        {t("dashboard.subscriptions")}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("dashboard.mrr")}
            value={subscriptionStats?.mrr ? `$${subscriptionStats.mrr}` : "$0"}
            icon={DollarSign}
            color="#2e7d32"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("dashboard.paidUsers")}
            value={subscriptionStats?.paid_users}
            icon={CreditCard}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("dashboard.paymentFailures")}
            value={subscriptionStats?.payment_failures}
            icon={AlertTriangle}
            color="#d32f2f"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card variant="outlined" sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
              {t("dashboard.userGrowth")}
            </Typography>
            {stats?.users_growth_data?.length > 0 ? (
              <Box sx={{ height: 320 }}>
                <Line data={chartData} options={chartOptions} />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                }}
              >
                <Typography>{t("dashboard.noGrowthData")}</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card variant="outlined" sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t("dashboard.recentActivity")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#1976d2",
                  color: "#fff",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats?.recent_activity?.new_users_last_30_days || 0}
                </Typography>
                <Typography variant="bodySm">
                  {t("dashboard.newUsersLast30")}
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#1976d2",
                  color: "#fff",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats?.recent_activity?.new_projects_last_30_days || 0}
                </Typography>
                <Typography variant="bodySm">
                  {t("dashboard.newProjectsLast30")}
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#1976d2",
                  color: "#fff",
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats?.recent_activity?.new_sources_last_30_days || 0}
                </Typography>
                <Typography variant="bodySm">
                  {t("dashboard.newSourcesLast30")}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ my: 4 }}>
        <Typography variant="h6" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
          {t("dashboard.quickActions")}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{ cursor: "pointer", "&:hover": { boxShadow: 4 } }}
              onClick={() => navigate("/admin/users")}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Users size={32} color="#1976d2" style={{ marginBottom: 8 }} />
                <Typography variant="h6">
                  {t("dashboard.manageUsers")}
                </Typography>
                <Typography variant="bodySm" color="text.secondary">
                  {t("dashboard.manageUsersDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{ cursor: "pointer", "&:hover": { boxShadow: 4 } }}
              onClick={() => navigate("/admin/projects")}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <FolderOpen
                  size={32}
                  color="#ed6c02"
                  style={{ marginBottom: 8 }}
                />
                <Typography variant="h6">
                  {t("dashboard.viewProjects")}
                </Typography>
                <Typography variant="bodySm" color="text.secondary">
                  {t("dashboard.viewProjectsDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{ cursor: "pointer", "&:hover": { boxShadow: 4 } }}
              onClick={() => navigate("/admin/sources")}
            >
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Database
                  size={32}
                  color="#2e7d32"
                  style={{ marginBottom: 8 }}
                />
                <Typography variant="h6">
                  {t("dashboard.dataSources")}
                </Typography>
                <Typography variant="bodySm" color="text.secondary">
                  {t("dashboard.dataSourcesDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ cursor: "pointer", "&:hover": { boxShadow: 4 } }}>
              <CardContent sx={{ textAlign: "center", py: 3 }}>
                <Activity
                  size={32}
                  color="#9c27b0"
                  style={{ marginBottom: 8 }}
                />
                <Typography variant="h6">{t("dashboard.analytics")}</Typography>
                <Typography variant="bodySm" color="text.secondary">
                  {t("dashboard.analyticsDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default Dashboard;
